import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";

const faqItems = [
  {
    q: "How long does setup take?",
    a: "It depends on portfolio size and mapping, but the goal is minutes to hours, not weeks.",
  },
  {
    q: "Can we pilot first?",
    a: "Yes. Start with a subset of properties or one market.",
  },
  {
    q: "Will this add more tasks for my staff?",
    a: "No. The integration exists to remove coordination, not create it.",
  },
  {
    q: "Is the guest experience branded?",
    a: "The experience is designed to align with your standard while Bellhopt runs behind the scenes.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function StreamlineIntegrationPage() {
  const title = "Streamline Integration | Bellhopt Concierge Layer for STRs";
  const description =
    "Connect Bellhopt with Streamline to automate pre-arrival ordering and concierge execution. Reservation-aware timing, delivery and stocking, and a premium arrival standard at scale.";
  const canonical = "https://bellhopt.com/integrations/streamline/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Bellhopt + Streamline",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "Integrations", item: "https://bellhopt.com/integrations/" },
        { "@type": "ListItem", position: 3, name: "Streamline", item: canonical },
      ],
    },
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical="/integrations/streamline/"
        robots="index,follow"
        ogTitle="Bellhopt + Streamline"
        ogDescription="A Streamline-connected concierge layer that turns reservations into arrival-ready stays."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto pt-24 pb-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            Bellhopt + Streamline
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            A Streamline-connected concierge layer that turns reservations into arrival-ready stays.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
              Schedule A Demo
            </a>
            <Link to="/integrations" className={SECONDARY_CTA}>
              Back To Integrations
            </Link>
          </div>
        </section>

        <div className="mt-6 space-y-6">
          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              What You Get with the Streamline Integration
            </h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {[
                { title: "Reservation-aware pre-arrival flow", desc: "Guests receive ordering at the right time before check-in." },
                { title: "Cleaner operations", desc: "No spreadsheets, no manual reminders, no chasing guest details." },
                { title: "Arrival execution at scale", desc: "Bellhopt handles delivery, access, stocking, and setup." },
                { title: "Portfolio-wide consistency", desc: "Standardize arrivals across every home you manage." },
              ].map((card) => (
                <div key={card.title} className="rounded-2xl border border-[#E5E7EB] p-5 bg-white/90">
                  <h3 className="font-segoe font-semibold text-[18px] text-[#111827]">{card.title}</h3>
                  <p className="mt-2 text-[15px] text-[#6B7280] leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <Section id="setup" title="Setup in Three Steps">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Connect Streamline</strong>
                <p>Authorize access and select the properties you want enabled.</p>
              </li>
              <li>
                <strong>Map properties and timing</strong>
                <p>Confirm property identifiers and the pre-arrival outreach window.</p>
              </li>
              <li>
                <strong>Go live</strong>
                <p>Bellhopt begins triggering the guest flow and executing arrivals.</p>
              </li>
            </ol>
          </Section>

          <Section id="data-used" title="What Data is Used">
            <p>Bellhopt uses reservation context to time the pre-arrival experience and fulfill requests correctly. We focus on what is necessary to execute arrival-ready stays.</p>
            <p className="mt-4 font-semibold text-[#111827]">Common categories:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Reservation dates and timing</li>
              <li>Property identifiers</li>
              <li>Guest contact context needed for the flow</li>
            </ul>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              What Your Team Does, and Does Not Do
            </h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-6">
              <div>
                <h3 className="font-segoe font-semibold text-[18px] text-[#111827] mb-3">Your team does</h3>
                <ul className="list-disc pl-6 text-[16px] text-[#6B7280] leading-7">
                  <li>Approve the launch scope</li>
                  <li>Confirm property mapping</li>
                  <li>Set the standard you want delivered</li>
                </ul>
              </div>
              <div>
                <h3 className="font-segoe font-semibold text-[18px] text-[#111827] mb-3">Your team does not</h3>
                <ul className="list-disc pl-6 text-[16px] text-[#6B7280] leading-7">
                  <li>Coordinate shopping and delivery</li>
                  <li>Manage access handoffs</li>
                  <li>Handle stocking or staging steps</li>
                  <li>Chase guests for last-minute details</li>
                </ul>
              </div>
            </div>
          </section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">{item.q}</summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Turn Streamline Reservations Into Arrival-Ready Stays
            </h2>
            <p className="mt-3 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Bellhopt is the concierge layer that plugs into Streamline and executes premium arrivals without adding operational complexity.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Schedule A Demo</a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>Talk To Sales</a>
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/integrations">All integrations</Link></li>
              <li><Link className="text-primary" to="/for/property-managers">For property managers</Link></li>
              <li><Link className="text-primary" to="/for/enterprise-property-managers">Enterprise property managers</Link></li>
              <li><Link className="text-primary" to="/how-it-works">How it works</Link></li>
              <li><Link className="text-primary" to="/pricing">Pricing</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </SeoMarketingShell>
  );
}

export default StreamlineIntegrationPage;
